import React from 'react';
import { figure2, figure3, figure4, figure7 } from '../assets';

function Figure({ src, alt, caption, widthClass = 'max-w-2xl' }) {
  return (
    <figure className="my-10 mx-auto card p-4 md:p-6 w-full">
      <div className={`mx-auto ${widthClass}`}>
        <img src={src} alt={alt} className="w-full h-auto rounded-lg" />
      </div>
      <figcaption className="figure-caption">{caption}</figcaption>
    </figure>
  );
}

function SSAHSection() {
  return (
    <section className="py-20 bg-white border-t border-ink-100">
      <div className="max-w-5xl mx-auto px-6">
        <p className="section-eyebrow mb-3">03 &middot; Hypothesis</p>
        <h2 className="section-title">Superficial Safety Alignment Hypothesis</h2>

        <div className="mt-10 prose-paper space-y-5 max-w-3xl mx-auto">
          <p>
            Previous research introduced the{' '}
            <strong>Superficial Alignment Hypothesis (SAH)</strong>, positing
            that a model's <em>knowledge and capabilities</em> are primarily
            learned during pretraining, while alignment teaches the model{' '}
            <em>which subdistribution of formats</em> to use when interacting
            with users. However, that hypothesis focuses on general alignment,
            making it hard to isolate pretraining from alignment when a model
            fails to meet user expectations.
          </p>
          <p>
            To specifically address safety alignment, we propose the{' '}
            <strong>Superficial Safety Alignment Hypothesis (SSAH)</strong>:
          </p>
        </div>

        {/* SSAH Statement Callout */}
        <div className="mt-8 max-w-3xl mx-auto">
          <div className="relative rounded-2xl bg-grad-callout p-[1px] shadow-card">
            <div className="rounded-2xl bg-white p-6 md:p-8">
              <div className="flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-500" />
                <span className="text-xs font-semibold uppercase tracking-widest text-brand-700">
                  SSAH
                </span>
              </div>
              <p className="mt-3 text-lg md:text-xl font-serif italic text-ink-800 leading-relaxed">
                Given an unsafe model that is capable of fulfilling users'
                malicious requests, <strong className="not-italic">safety alignment</strong> teaches
                the model the correct <strong className="not-italic">reasoning direction</strong>{' '}
                and a simple refusal mechanism with reserved options.
              </p>
            </div>
          </div>
        </div>

        <div className="prose-paper max-w-3xl mx-auto mt-8 space-y-5">
          <p>
            <strong>Reasoning direction</strong> refers to the model's internal
            decision-making process when responding to malicious queries. It
            represents the binary choice between fulfilling a harmful request
            and issuing a refusal.
          </p>
        </div>

        {/* Key Differences */}
        <h3 className="subsection-title text-center">Key Differences from SAH</h3>
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          {[
            {
              t: 'Knowledge & reasoning',
              b: 'SSAH assumes models already possess sufficient knowledge and reasoning, so safety alignment focuses solely on safe behavior.',
            },
            {
              t: 'Refusal mechanisms',
              b: 'Standardized refusals with fallback options make the task more tractable than handling diverse human preferences.',
            },
            {
              t: 'Reasoning direction',
              b: 'SSAH teaches the model to consistently choose the correct direction — a binary classification per step.',
            },
          ].map((d, i) => (
            <div key={d.t} className="card p-5 hover:shadow-card transition">
              <div className="text-xs font-bold text-brand-600">0{i + 1}</div>
              <div className="mt-2 font-semibold text-ink-900 font-serif">{d.t}</div>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">{d.b}</p>
            </div>
          ))}
        </div>

        {/* Challenges */}
        <h3 className="subsection-title text-center">Challenges in Proving SSAH</h3>
        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            Empirically proving SSAH is challenging due to the infeasibility of
            sampling enough outputs to capture the model's full response
            distribution. Surface-level benchmarks alone are insufficient.
          </p>
          <p>
            We therefore take an <strong>alternative approach</strong>:{' '}
            <span className="font-semibold text-accent-600">if SSAH holds</span>,
            we should observe distinct and consistent differences in reasoning
            direction at <em>each generation step</em> between safety-aligned
            and non-aligned models. Probing the model's reasoning direction
            gives deeper insight than output benchmarks alone.
          </p>
        </div>

        {/* Probing Experiment */}
        <h3 className="subsection-title text-center">Probing Experiment</h3>
        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            We compare <strong>hidden state distances</strong> in feature space
            across three types of queries:
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-6 max-w-4xl mx-auto">
          {[
            { tag: 'Query', text: 'A malicious query (e.g., "How to make a bomb?").' },
            {
              tag: 'Query + benign tokens',
              text: 'The malicious query followed by benign tokens (e.g., "Sorry, I can’t...").',
            },
            {
              tag: 'Query + malicious tokens',
              text: 'The malicious query followed by malicious tokens (e.g., "Here’s how...").',
            },
          ].map((item) => (
            <div key={item.tag} className="card p-5">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-600">
                {item.tag}
              </div>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        <div className="prose-paper max-w-3xl mx-auto mt-6">
          <p>
            By comparing the distances between hidden states, we gain insight
            into how safety alignment reshapes the model's decision-making
            during token generation. Aligned models should show shorter
            distances between <strong>Query</strong> and{' '}
            <strong>Query + benign prompt tokens</strong>.
          </p>
        </div>

        <Figure
          src={figure2}
          alt="Probing experiment design"
          caption="Figure 2. Probing the model's reasoning direction via hidden-state distances."
          widthClass="max-w-xl"
        />

        <Figure
          src={figure7}
          alt="Reasoning direction across queries"
          caption="Figure 7. Reasoning-direction signal across query types."
          widthClass="max-w-xl"
        />

        {/* Results Analysis */}
        <h3 className="subsection-title text-center">Results Analysis</h3>
        <div className="max-w-3xl mx-auto">
          <ul className="space-y-3">
            {[
              <>
                In <strong>aligned</strong> models, the distance between{' '}
                <strong>Query</strong> and <strong>Query + benign tokens</strong>{' '}
                is consistently shorter than to <strong>Query + malicious tokens</strong>.
              </>,
              <>
                In <strong>unaligned</strong> models, the opposite pattern
                emerges &mdash; no strong preference for safe reasoning.
              </>,
              <>
                Aligned models exhibit clear and consistent safe reasoning
                preferences across all transformer blocks; unaligned models do
                not.
              </>,
            ].map((c, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-ink-700 leading-relaxed"
              >
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand-500 shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>

        <Figure
          src={figure3}
          alt="Distance per transformer block"
          caption="Figure 3. Hidden-state distance per transformer block."
          widthClass="max-w-md"
        />

        {/* Discussion */}
        <h3 className="subsection-title text-center">Discussion and Implications</h3>
        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            Safety alignment not only influences higher-level features in later
            layers but also embeds safe reasoning preferences in earlier layers
            of the transformer architecture. It operates at multiple levels,
            reshaping internal decision-making throughout generation.
          </p>
          <p className="text-ink-800">
            <strong>
              These findings provide strong evidence for SSAH but do not fully
              capture the nuanced changes introduced by safety alignment.
              Further research is needed to explore other potential effects and
              limitations.
            </strong>
          </p>
        </div>

        <Figure
          src={figure4}
          alt="Additional visual"
          caption="Figure 4. Supplementary visualization."
          widthClass="max-w-md"
        />
      </div>
    </section>
  );
}

export default SSAHSection;
